import classNames from "classnames";
import React, { FC, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { useInviteToPortalMutation } from "../../slices/ClientPortalSlice";
import { Avatar } from "../portal/components/PortalUi";
import { ClientPortalOverview } from "./clientPortal.types";
import {
  DEFAULT_INVITE_MESSAGE,
  isEmailAddress,
  personName,
  possessive,
  splitName,
} from "./clientPortal.utils";
import {
  errorText,
  modalCancel,
  modalInput,
  modalPrimary,
  PortalModal,
} from "./PortalModal";

type Added = { name: string; email: string };

/** Pick the client's contacts (or type someone in) and email them a portal invitation. */
export const InviteToPortalModal: FC<{
  overview: ClientPortalOverview;
  onClose: () => void;
}> = ({ overview, onClose }) => {
  const candidates = overview.people.filter((p) => p.status !== "active");
  const [picked, setPicked] = useState<Set<string>>(
    () =>
      new Set(
        candidates.filter((p) => p.status === "none").map((p) => p.email),
      ),
  );
  const [added, setAdded] = useState<Added[]>([]);
  const [adding, setAdding] = useState(overview.people.length === 0);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [message, setMessage] = useState(
    DEFAULT_INVITE_MESSAGE(overview.clientName),
  );
  const [error, setError] = useState<string | null>(null);
  const [invite, inviteState] = useInviteToPortalMutation();

  const total = picked.size + added.length;
  const toggle = (email: string) =>
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });

  const addPerson = () => {
    setError(null);
    const email = newEmail.trim().toLowerCase();
    if (!isEmailAddress(email)) return setError("Enter a valid email address.");
    if (
      overview.people.some((p) => p.email === email) ||
      added.some((a) => a.email === email)
    )
      return setError("This person is already in the list.");
    setAdded((a) => [...a, { name: newName.trim(), email }]);
    setNewName("");
    setNewEmail("");
  };

  const people = useMemo(
    () => [
      ...overview.people
        .filter((p) => picked.has(p.email))
        .map((p) => ({
          email: p.email,
          firstName: p.firstName ?? undefined,
          lastName: p.lastName ?? undefined,
        })),
      ...added.map((a) => ({ email: a.email, ...splitName(a.name) })),
    ],
    [overview.people, picked, added],
  );

  const send = async () => {
    setError(null);
    if (!people.length) return setError("Pick at least one person to invite.");
    const result = await invite({
      customerId: overview.customerId,
      people,
      message: message.trim() || undefined,
    });
    if ("error" in result) return setError(errorText(result.error));
    const { sent, failed } = result.data;
    if (failed.length) {
      setError(
        `${sent ? `${sent} sent. ` : ""}The email to ${failed.join(
          ", ",
        )} couldn't be sent. Try again with “Resend”.`,
      );
      return;
    }
    toast(sent === 1 ? "Invitation sent" : `${sent} invitations sent`, {
      type: "success",
    });
    onClose();
  };

  return (
    <PortalModal
      title={"Invite to client portal"}
      subtitle={`${overview.clientName} · pick the contacts who should get access`}
      onClose={onClose}
      footer={
        <>
          <button type={"button"} className={modalCancel} onClick={onClose}>
            Cancel
          </button>
          <button
            type={"button"}
            className={modalPrimary}
            onClick={send}
            disabled={inviteState.isLoading || total === 0}>
            {inviteState.isLoading
              ? "Sending…"
              : total === 1
              ? "Send 1 invitation"
              : `Send ${total} invitations`}
          </button>
        </>
      }>
      <div className={"flex flex-col gap-2"}>
        {overview.people.map((p) => {
          const active = p.status === "active";
          const on = picked.has(p.email);
          const name = personName(p);
          return (
            <label
              key={p.email}
              className={classNames(
                "flex items-center gap-3 rounded-[10px] border px-3.5 py-3",
                active
                  ? "cursor-default border-[#EDEFF3] bg-[#FAFAFB]"
                  : on
                  ? "cursor-pointer border-[#CFC6FF] bg-[#FAF9FF]"
                  : "cursor-pointer border-[#EDEFF3] hover:bg-[#FAFAFC]",
              )}>
              <input
                type={"checkbox"}
                className={"h-4 w-4 accent-[#6B4EFF]"}
                disabled={active}
                checked={active || on}
                onChange={() => toggle(p.email)}
              />
              <Avatar name={name} size={32} />
              <span className={"flex min-w-0 flex-1 flex-col"}>
                <span className={"truncate text-[13.5px] font-bold"}>
                  {name}
                </span>
                <span className={"truncate text-xs font-medium text-[#6B7185]"}>
                  {p.email}
                </span>
              </span>
              {active && (
                <span
                  className={
                    "whitespace-nowrap text-xs font-bold text-[#147A4B]"
                  }>
                  Already has access
                </span>
              )}
              {p.status === "invited" && (
                <span
                  className={
                    "whitespace-nowrap text-xs font-bold text-[#8A5A00]"
                  }>
                  Invite pending · resend
                </span>
              )}
              {p.status === "revoked" && (
                <span
                  className={
                    "whitespace-nowrap text-xs font-bold text-[#6B7185]"
                  }>
                  Removed · invite again
                </span>
              )}
            </label>
          );
        })}
        {added.map((a) => (
          <div
            key={a.email}
            className={
              "flex items-center gap-3 rounded-[10px] border border-[#CFC6FF] bg-[#FAF9FF] px-3.5 py-3"
            }>
            <input
              type={"checkbox"}
              className={"h-4 w-4 accent-[#6B4EFF]"}
              checked={true}
              onChange={() =>
                setAdded((x) => x.filter((y) => y.email !== a.email))
              }
            />
            <Avatar name={a.name || a.email} size={32} />
            <span className={"flex min-w-0 flex-1 flex-col"}>
              <span className={"truncate text-[13.5px] font-bold"}>
                {a.name || a.email}
              </span>
              <span className={"truncate text-xs font-medium text-[#6B7185]"}>
                {a.email}
              </span>
            </span>
          </div>
        ))}
        {overview.people.length === 0 && added.length === 0 && (
          <span className={"text-[13px] font-medium text-[#6B7185]"}>
            This client has no contacts with an email address yet. Add the
            person to invite below.
          </span>
        )}
        {adding ? (
          <div
            className={
              "flex flex-col gap-2 rounded-[10px] border border-dashed border-[#DCE2EA] p-3"
            }>
            <div className={"grid grid-cols-1 gap-2 sm:grid-cols-2"}>
              <input
                className={modalInput}
                placeholder={"Name"}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                aria-label={"Name"}
              />
              <input
                className={modalInput}
                placeholder={"Email"}
                type={"email"}
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                aria-label={"Email"}
                onKeyDown={(e) =>
                  e.key === "Enter" && (e.preventDefault(), addPerson())
                }
              />
            </div>
            <div className={"flex justify-end"}>
              <button
                type={"button"}
                className={"text-[13px] font-bold text-[#6B4EFF]"}
                onClick={addPerson}>
                Add to the list
              </button>
            </div>
          </div>
        ) : (
          <button
            type={"button"}
            className={"self-start text-[13px] font-bold text-[#6B4EFF]"}
            onClick={() => setAdding(true)}>
            + Add someone who isn’t a contact yet
          </button>
        )}
      </div>

      <div className={"flex flex-col gap-1.5"}>
        <label
          htmlFor={"portal-invite-message"}
          className={"text-[13px] font-bold text-[#3A4256]"}>
          Message in the invitation email
        </label>
        <textarea
          id={"portal-invite-message"}
          rows={3}
          maxLength={2000}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={
            "w-full resize-none rounded-[9px] border-[1.5px] border-[#DCE2EA] px-3 py-2.5 text-[13.5px] leading-relaxed outline-none focus:border-[#6B4EFF]"
          }
        />
      </div>
      <div
        className={
          "rounded-[10px] bg-[#F7F8FA] px-3.5 py-3 text-[12.5px] font-medium leading-relaxed text-[#6B7185]"
        }>
        Each person picks how to sign in: a password, or a one-time code sent to
        their email.
        {overview.isOrganization
          ? ` Everyone invited sees all of ${possessive(
              overview.clientName,
            )} portal files.`
          : ""}
      </div>
      {error && (
        <div
          role={"alert"}
          className={
            "rounded-[9px] bg-[#FDECEC] px-3.5 py-2.5 text-[13px] font-semibold text-[#B42318]"
          }>
          {error}
        </div>
      )}
    </PortalModal>
  );
};
